import { useState, useEffect, createContext } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';

import { sampleAPI } from '../apis/sampleAPI';
import { useSet } from '../hook/useSet';
import { useStorage } from '../hook/useStorage';

export const SampleContext = createContext(null);

export const SampleProvider = ({ children }) => {
	const params = useParams();

	const [activeSample, setActiveSample] = useState(null);
	const [updatedSample, setUpdatedSample] = useState(null);
	const [samples, setSamples] = useState({});
	const [loading, setLoading] = useState(true);

	const { activeStorage, refreshStorages } = useStorage();

	const { sets } = useSet();

	useEffect(() => {
		console.log('Initialization: SampleProvider');
	}, []);

	useEffect(() => {
		if (activeStorage === null) {
			setActiveSample(null);
			setSamples({});
			setLoading(false);
		} else {
			setLoading(true);
			sampleAPI.get(activeStorage.id).then((resp) => {
				let samplesExt = extendSamples(resp?.samples, resp?.cards, sets);
				setSamples(samplesExt);
				setLoading(false);
			});
		}
	}, [activeStorage, sets]);

	useEffect(() => {
		let sampleUpd = null;
		if (params.sample_id in samples) {
			sampleUpd = samples[params.sample_id];
		}
		setActiveSample(sampleUpd);
	}, [samples, params.sample_id]);

	function selectSample(sample) {
		setActiveSample(sample);
	}

	function addSample(sample_id, storage_id, qty, condition, finish, note, language, onResult) {
		sampleAPI.add(sample_id, storage_id, qty, condition, finish, note, language).then((resp) => {
			if (resp) {
				let sampleUpd = resp.sample;
				sampleUpd.set = resp.card.set_id in sets ? sets[resp.card.set_id] : null;
				sampleUpd.card = resp.card;

				let samplesUpd = { ...samples };
				samplesUpd[sampleUpd.id] = sampleUpd;
				setSamples(samplesUpd);
				setUpdatedSample(sampleUpd);

				refreshStorages([resp.storage]);

				onResult(sampleUpd);
			} else {
				onResult(null);
			}
		});
	}

	function updateSample(sample_id, storage_id, qty, condition, finish, note, language, onResult) {
		sampleAPI.update(sample_id, storage_id, qty, condition, finish, note, language).then((resp) => {
			if (resp) {
				let sampleUpd = resp.sample;
				sampleUpd.set = resp.card.set_id in sets ? sets[resp.card.set_id] : null;
				sampleUpd.card = resp.card;

				let samplesUpd = { ...samples };
				delete samplesUpd[sampleUpd.id];
				samplesUpd[sampleUpd.id] = sampleUpd;
				setSamples(samplesUpd);
				setUpdatedSample(sampleUpd);

				if (activeSample?.id === sampleUpd.id) {
					setActiveSample(sampleUpd);
				}

				refreshStorages([resp.storage]);

				onResult(sampleUpd);
			} else {
				onResult(null);
			}
		});
	}

	function moveSamples(sampleIds, targetId, onResult) {
		sampleAPI.move(sampleIds, targetId).then((resp) => {
			if (resp) {
				let samplesUpd = { ...samples };
				let samplesExt = extendSamples(resp?.samples, resp?.cards, sets);
				samplesUpd = { ...samplesUpd, ...samplesExt };
				// Delete samples
				for (let i = 0; i < resp.samplesDel.length; i++) {
					const sampleId = resp.samplesDel[i].id;
					delete samplesUpd[sampleId];
				}
				setSamples(samplesUpd);
				refreshStorages(resp.storages);
				onResult(true);
			} else {
				onResult(false);
			}
		});
	}

	function deleteSample(sampleId, onResult) {
		sampleAPI.delete(sampleId).then((resp) => {
			if (resp) {
				let samplesUpd = { ...samples };
				delete samplesUpd[sampleId];
				setSamples(samplesUpd);
				if (activeSample?.id === sampleId) {
					setActiveSample(null);
				}

				refreshStorages([resp.storage]);

				onResult(true);
			} else {
				onResult(false);
			}
		});
	}

	function getSamplesForCard(cardId, onResult) {
		sampleAPI.getSamplesForCard(cardId).then((resp) => {
			if (resp) {
				let cards = {};
				for (let card of resp.cards) {
					cards[card.id] = card;
				}
				// Apply extended samples to cache
				let samplesUpd = {};
				for (let i = 0; i < resp.samples.length; i++) {
					let sample = resp.samples[i];
					sample.card = sample.card_id in cards ? cards[sample.card_id] : null;
					sample.set = sample.card && sample.card.set_id in sets ? sets[sample.card.set_id] : null;
					samplesUpd[sample.id] = sample;
				}
				setSamples(samplesUpd);
				onResult(samplesUpd);
			} else {
				setSamples({});
				onResult({});
			}
		});
	}

	const value = {
		activeSample,
		updatedSample,
		samples,
		selectSample,
		addSample,
		updateSample,
		moveSamples,
		deleteSample,
		getSamplesForCard,
		loadingSample: loading,
	};

	return <SampleContext.Provider value={value}>{children}</SampleContext.Provider>;
};

/**
 * Extends raw sample objects by attaching corresponding card and set objects.
 *
 * @param {Array<Object>} samples_raw - Array of raw sample objects
 * @param {Array<Object>} cards_raw - Array of card objects
 * @param {Object} sets - Object mapping set IDs to set objects.
 * @returns {Object} An object mapping sample IDs to extended sample objects, each with attached `card` and `set` objects.
 */
function extendSamples(samples_raw, cards_raw, sets) {
	if (!samples_raw || !cards_raw) return {};

	let cards_by_id = {};
	for (let card of cards_raw) {
		cards_by_id[card.id] = card;
	}

	let samplesExt = {};
	for (let sample of samples_raw) {
		sample.card = sample.card_id in cards_by_id ? cards_by_id[sample.card_id] : null;
		sample.set = sample.card && sample.card.set_id in sets ? sets[sample.card.set_id] : null;
		samplesExt[sample.id] = sample;
	}

	return samplesExt;
}

SampleProvider.propTypes = {
	children: PropTypes.node.isRequired,
};
